/* TENSO — fiber journey scroller.

   The rail is a native overflow-x + scroll-snap element. This file adds three
   things on top of it: the counter, tap/click-to-advance, and — on fine-pointer
   devices only — an opt-in wheel capture that steps one frame per gesture.

   Nothing here controls whether content is visible. If this file fails to load,
   every frame is still reachable by swiping, dragging or arrow keys; you lose
   the counter, the tap shortcut and the wheel stepping. That is deliberate —
   two earlier scroll-driven attempts on this section shipped invisible content.

   The wheel capture is scroll-jacking, so it is fenced hard:
     - it never engages until the user clicks a frame (explicit opt-in)
     - it releases the moment a fresh gesture pushes past either end
     - it releases on an outside click, on Escape, and when the section
       leaves the viewport
     - momentum events after a flick can never trigger the release, so the
       page cannot lurch mid-gesture
*/
(function(){
  "use strict";

  var FINE   = !!(window.matchMedia && window.matchMedia('(pointer:fine)').matches);
  var REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* one wheel step per gesture; momentum inside this window is swallowed */
  var COOLDOWN = 520;
  /* a pointer that travelled further than this was a drag, not a tap */
  var TAP_SLOP = 8;
  var TAP_TIME = 600;

  function pad(n){ return (n < 10 ? '0' : '') + n; }

  function build(wrap){
    if(wrap.dataset.fjlBound) return;
    wrap.dataset.fjlBound = '1';

    var rail = wrap.querySelector('.fjl');
    if(!rail) return;
    var cards = [].slice.call(rail.querySelectorAll('.fjl__card'));
    var cur   = wrap.querySelector('[data-fjl-cur]');
    var bar   = wrap.querySelector('.fjl__bar');
    var N     = cards.length;
    if(!N) return;

    if(bar) bar.style.width = (100 / N) + '%';

    /* ---------- geometry ---------- */

    function frameWidth(){ return rail.clientWidth || 1; }

    /* RTL engines report scrollLeft negative, so read through Math.abs and
       write back through the same sign. */
    function rtlSign(){
      return (window.getComputedStyle(rail).direction === 'rtl') ? -1 : 1;
    }

    function currentIndex(){
      var i = Math.round(Math.abs(rail.scrollLeft) / frameWidth());
      if(i < 0) i = 0;
      if(i > N - 1) i = N - 1;
      return i;
    }

    function goTo(i, instant){
      if(i < 0) i = 0;
      if(i > N - 1) i = N - 1;
      var behavior = (instant || REDUCE) ? 'auto' : 'smooth';
      var left = rtlSign() * i * frameWidth();
      if(rail.scrollTo) rail.scrollTo({ left: left, behavior: behavior });
      else rail.scrollLeft = left;
      wrap.classList.add('is-moved');
    }

    /* ---------- counter + hairline ---------- */

    var ticking = false;
    function paint(){
      ticking = false;
      var i = currentIndex();
      if(cur) cur.textContent = pad(i + 1);
      if(bar) bar.style.transform = 'translateX(' + (i * 100) + '%)';
      if(Math.abs(rail.scrollLeft) > 6) wrap.classList.add('is-moved');
    }
    function onScroll(){
      if(ticking) return;
      ticking = true;
      window.requestAnimationFrame(paint);
    }
    rail.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    paint();

    /* ---------- advance ---------- */

    function advance(){
      var i = currentIndex();
      /* last frame wraps back to the first — instantly, so it reads as a return
         to the start rather than a long pan back across nine images */
      if(i >= N - 1) goTo(0, true);
      else goTo(i + 1, false);
    }

    /* ---------- armed state (fine pointer only) ---------- */

    var armed = false, cooling = false, coolTimer = null;

    function arm(){
      if(armed || !FINE) return;
      armed = true;
      wrap.classList.add('is-armed');
    }
    function disarm(){
      if(!armed) return;
      armed = false;
      cooling = false;
      clearTimeout(coolTimer);
      wrap.classList.remove('is-armed');
    }

    /* ---------- tap / click to advance ---------- */

    var down = false, downX = 0, downY = 0, downT = 0, moved = false;

    rail.addEventListener('pointerdown', function(e){
      down = true; moved = false;
      downX = e.clientX; downY = e.clientY; downT = Date.now();
    }, { passive: true });

    rail.addEventListener('pointermove', function(e){
      if(!down) return;
      if(Math.abs(e.clientX - downX) > TAP_SLOP ||
         Math.abs(e.clientY - downY) > TAP_SLOP) moved = true;
    }, { passive: true });

    function endTap(e){
      if(!down) return;
      down = false;
      if(moved) return;                                  /* that was a swipe */
      if(Date.now() - downT > TAP_TIME) return;          /* that was a hold  */
      if(e.target && e.target.closest && e.target.closest('a,button')) return;
      advance();
      arm();
    }
    rail.addEventListener('pointerup', endTap, { passive: true });
    rail.addEventListener('pointercancel', function(){ down = false; }, { passive: true });

    ['pointerdown','keydown','touchstart'].forEach(function(ev){
      rail.addEventListener(ev, function(){ wrap.classList.add('is-moved'); },
        { once: true, passive: true });
    });

    /* ---------- wheel capture ---------- */

    wrap.addEventListener('wheel', function(e){
      if(!armed) return;

      /* a genuinely horizontal gesture belongs to the native rail */
      if(Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      /* Swallow momentum first, before the boundary test. If the boundary were
         tested during momentum, the tail of a flick would release the capture
         and the page would jump mid-gesture. */
      if(cooling){ e.preventDefault(); return; }

      var i = currentIndex();
      var forward = e.deltaY > 0;

      /* a fresh gesture pushing past either end hands the wheel back */
      if(forward && i >= N - 1){ disarm(); return; }
      if(!forward && i <= 0){ disarm(); return; }

      e.preventDefault();
      cooling = true;
      clearTimeout(coolTimer);
      coolTimer = setTimeout(function(){ cooling = false; }, COOLDOWN);
      goTo(i + (forward ? 1 : -1), false);
    }, { passive: false });

    /* ---------- release ---------- */

    document.addEventListener('pointerdown', function(e){
      if(armed && !wrap.contains(e.target)) disarm();
    }, { passive: true });

    document.addEventListener('keydown', function(e){
      if(e.key === 'Escape' || e.keyCode === 27) disarm();
    });

    /* held in a property so the observer outlives this function */
    if('IntersectionObserver' in window){
      wrap._fjlIO = new IntersectionObserver(function(entries){
        for(var k = 0; k < entries.length; k++){
          if(!entries[k].isIntersecting) disarm();
        }
      }, { threshold: 0 });
      wrap._fjlIO.observe(wrap);
    }
  }

  function init(){
    document.querySelectorAll('[data-fjl-wrap]').forEach(build);
  }
  if(document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
